import { supabase } from "../../config/supabase";
import { useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import {
    startOfMonth,
    endOfMonth,
    addMonths,
    eachDayOfInterval,
    format,
    isSameDay,
} from "date-fns";
import { FiCalendar, FiChevronLeft, FiChevronRight, FiFilter, FiCheck } from "react-icons/fi";
import { getMonthlyInvoiceAssignments } from "../../lib/supabase/monthlySchedule";
import { getInvoiceSourceIcon, getInvoiceStatusText } from "../../lib/common/invoiceDisplay";

const INVOICE_STYLES = {
    not_invoiced: "text-red-800 bg-red-200",
    no_xero_invoice: "text-red-800 bg-red-200",
    deleted: "text-red-800 bg-red-200",

    draft: "text-blue-800 bg-blue-200",
    submitted: "text-blue-800 bg-blue-200",
    needs_approval: "text-blue-800 bg-blue-200",
    pending: "text-blue-800 bg-blue-200",

    deposit: "text-yellow-800 bg-yellow-200",

    invoiced: "text-green-800 bg-green-200",
    authorised: "text-green-800 bg-green-200",
    cod: "text-green-800 bg-green-200",
    paid: "text-green-800 bg-green-200",

    voided: "text-zinc-800 bg-zinc-200",
    cancelled: "text-zinc-800 bg-zinc-200",
};

const NOTIFY_FILTER_OPTIONS = [
    { key: "not_invoiced", label: "Not Invoiced" },
    { key: "invoiced", label: "Invoiced" },
    { key: "cod", label: "COD" },
    { key: "paid", label: "Paid" },
    { key: "deposit", label: "Deposit" },
    { key: "pending", label: "Pending" },
    { key: "voided", label: "Voided" },
];

const XERO_FILTER_OPTIONS = [
    { key: "no_xero_invoice", label: "No Xero Invoice" },
    { key: "draft", label: "Draft" },
    { key: "submitted", label: "Submitted" },
    { key: "needs_approval", label: "Needs Approval" },
    { key: "authorised", label: "Authorised" },
    { key: "invoiced", label: "Invoiced" },
    { key: "paid", label: "Paid" },
    { key: "voided", label: "Voided" },
    { key: "cancelled", label: "Cancelled" },
    { key: "deleted", label: "Deleted" },
];


const INVOICE_SORT_ORDER = {
    not_invoiced: 0,
    no_xero_invoice: 1,
    deleted: 2,

    draft: 3,
    submitted: 4,
    needs_approval: 5,
    pending: 6,

    deposit: 7,

    invoiced: 8,
    authorised: 9,
    cod: 10,

    paid: 11,

    voided: 12,
    cancelled: 13,
};

const FILTER_STORAGE_KEY = "monthlyInvoiceView_invoiceFilters";
const MONTH_STORAGE_KEY = "monthlyInvoiceView_selectedMonth";

const normalizeInvoiceKey = (statusText) => {
    if (!statusText) return "not_invoiced";

    const key = String(statusText)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");

    return key || "not_invoiced";
};

const getQuoteDate = (assignment) => {
    const date =
        assignment?.quote_info?.expected_delivery_date ||
        assignment?.quote_info?.expected_finish_date;

    return date ? new Date(date) : null;
};

const isDateInsideSelectedMonth = (dateValue, selectedMonth) => {
    if (!dateValue) return false;

    const date = new Date(dateValue);
    const monthStart = startOfMonth(selectedMonth);
    const monthEnd = endOfMonth(selectedMonth);

    return date >= monthStart && date <= monthEnd;
};

const getQuoteRealtimeDate = (quote) => {
    return quote?.expected_delivery_date || quote?.expected_finish_date;
};

const MonthlyInvoiceView = ({ company, quoteType, cancelledFilter = 'active_only' }) => {
    const [selectedMonth, setSelectedMonth] = useState(() => {
        try {
            const stored = localStorage.getItem(MONTH_STORAGE_KEY);

            if (stored) {
                const parsed = new Date(stored);
                if (!Number.isNaN(parsed.getTime())) {
                    return startOfMonth(parsed);
                }
            }
        } catch { }

        return startOfMonth(new Date());
    });

    useEffect(() => {
        try {
            localStorage.setItem(
                MONTH_STORAGE_KEY,
                format(startOfMonth(selectedMonth), "yyyy-MM-dd")
            );
        } catch { }
    }, [selectedMonth]);


    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterOpen, setFilterOpen] = useState(false);
    const filterRef = useRef(null);


    const invoiceFilterOptions = useMemo(() => {
        const mode = company?.settings?.invoice_display_mode || "notify";
        const textMode = company?.settings?.invoice_text_mode;

        if (mode === "notify") {
            return NOTIFY_FILTER_OPTIONS;
        }

        if (mode === "xero") {
            return textMode === "xero"
                ? XERO_FILTER_OPTIONS.filter((item) => item.key !== "needs_approval" && item.key !== "invoiced" && item.key !== "cancelled")
                : XERO_FILTER_OPTIONS.filter((item) => item.key !== "submitted" && item.key !== "authorised" && item.key !== "voided");
        }

        return [
            ...NOTIFY_FILTER_OPTIONS,
            ...(textMode === "xero"
                ? XERO_FILTER_OPTIONS.filter((item) =>
                    ["no_xero_invoice", "draft", "submitted", "authorised", "paid", "voided", "deleted"].includes(item.key)
                )
                : XERO_FILTER_OPTIONS.filter((item) =>
                    ["no_xero_invoice", "draft", "needs_approval", "invoiced", "paid", "cancelled", "deleted"].includes(item.key)
                )),
        ].filter((item, index, arr) => arr.findIndex((x) => x.key === item.key) === index);
    }, [company]);

    const [enabledInvoiceStatuses, setEnabledInvoiceStatuses] = useState(() => {
        try {
            const stored = localStorage.getItem(FILTER_STORAGE_KEY);
            if (stored) return JSON.parse(stored);
        } catch { }

        return invoiceFilterOptions.reduce((acc, item) => {
            acc[item.key] = true;
            return acc;
        }, {});
    });


    useEffect(() => {
        try {
            localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(enabledInvoiceStatuses));
        } catch { }
    }, [enabledInvoiceStatuses]);

    useEffect(() => {
        if (!filterOpen) return;

        const handleClickOutside = (event) => {
            if (filterRef.current && !filterRef.current.contains(event.target)) {
                setFilterOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [filterOpen]);

    const days = useMemo(() => {
        return eachDayOfInterval({
            start: startOfMonth(selectedMonth),
            end: endOfMonth(selectedMonth),
        });
    }, [selectedMonth]);

    useEffect(() => {
        const fetchMonth = async () => {
            if (!company?.id || !quoteType) return;

            setLoading(true);

            const monthStart = format(startOfMonth(selectedMonth), "yyyy-MM-dd");
            const monthEnd = format(addMonths(startOfMonth(selectedMonth), 1), "yyyy-MM-dd");

            const { data, error } = await getMonthlyInvoiceAssignments(company.id, quoteType, {
                monthStart,
                monthEnd,
            });

            console.log(data)

            if (error) {
                console.error("Monthly invoice fetch failed:", error);
                setAssignments([]);
            } else {
                setAssignments(data || []);
            }

            setLoading(false);
        };

        fetchMonth();
    }, [company?.id, quoteType, selectedMonth]);

    useEffect(() => {
        if (!company?.id || !quoteType) return;

        const channel = supabase
            .channel(`monthly-invoice-view-${company.id}-${quoteType}`)
            .on(
                "postgres_changes",
                {
                    event: "UPDATE",
                    schema: "public",
                    table: "jobs",
                    filter: `company_id=eq.${company.id}`,
                },
                (payload) => {
                    const updatedJob = payload.new;

                    setAssignments((prev) =>
                        prev.map((assignment) => {
                            if (assignment.id !== updatedJob.id) return assignment;

                            return {
                                ...assignment,
                                unique_id: updatedJob.unique_id,
                                customer_name: updatedJob.customer_name ?? assignment.customer_name,
                                customer_address: updatedJob.customer_address ?? assignment.customer_address,
                                is_cancelled: updatedJob.is_cancelled,
                            };
                        })
                    );
                }
            )
            .on(
                "postgres_changes",
                {
                    event: "UPDATE",
                    schema: "public",
                    table: "job_quotes",
                    filter: `quote_type=eq.${quoteType}`,
                },
                (payload) => {
                    const updatedQuote = payload.new;

                    setAssignments((prev) => {
                        const existsInCurrentList = prev.some(
                            (assignment) => assignment.quote_info?.id === updatedQuote.id
                        );

                        if (!existsInCurrentList) return prev;

                        const quoteDate = getQuoteRealtimeDate(updatedQuote);
                        const shouldStayInMonth = isDateInsideSelectedMonth(
                            quoteDate,
                            selectedMonth
                        );
                        const isPlayground =
                            updatedQuote.quote_playground === true ||
                            updatedQuote.delivery_playground === true;

                        if (!shouldStayInMonth || isPlayground) {
                            return prev.filter(
                                (assignment) => assignment.quote_info?.id !== updatedQuote.id
                            );
                        }

                        return prev.map((assignment) => {
                            if (assignment.quote_info?.id !== updatedQuote.id) return assignment;

                            return {
                                ...assignment,
                                quote_info: {
                                    ...assignment.quote_info,
                                    ...updatedQuote,
                                },
                            };
                        });
                    });
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [company?.id, quoteType, selectedMonth]);

    const preparedAssignments = useMemo(() => {
        return assignments
            .filter((assignment) => {
                const cancelled = !!assignment.is_cancelled;

                if (cancelledFilter === 'cancelled_only') return cancelled;
                if (cancelledFilter === 'active_cancelled') return true;

                return !cancelled;
            })
            .map((assignment) => {
                const statusText = getInvoiceStatusText(assignment.quote_info, company);
                const statusKey = normalizeInvoiceKey(statusText);

                return {
                    ...assignment,
                    invoiceStatusText: statusText,
                    invoiceStatusKey: statusKey,
                };
            })
            .filter((assignment) => enabledInvoiceStatuses[assignment.invoiceStatusKey] !== false)
            .sort((a, b) => {
                const aOrder = INVOICE_SORT_ORDER[a.invoiceStatusKey] ?? 999;
                const bOrder = INVOICE_SORT_ORDER[b.invoiceStatusKey] ?? 999;

                if (aOrder !== bOrder) return aOrder - bOrder;

                const aCode = a.quote_info?.child_unique_id || a.unique_id || "";
                const bCode = b.quote_info?.child_unique_id || b.unique_id || "";

                return aCode.localeCompare(bCode, undefined, { numeric: true });
            });
    }, [assignments, company, enabledInvoiceStatuses, cancelledFilter]);

    const assignmentsByDay = useMemo(() => {
        const map = {};

        days.forEach((day) => {
            const key = format(day, "yyyy-MM-dd");
            map[key] = preparedAssignments.filter((assignment) => {
                const quoteDate = getQuoteDate(assignment);
                return quoteDate && isSameDay(quoteDate, day);
            });
        });

        return map;
    }, [preparedAssignments, days]);

    const enabledCount = Object.values(enabledInvoiceStatuses).filter(Boolean).length;

    const toggleInvoiceStatus = (key) => {
        setEnabledInvoiceStatuses((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const setAllFilters = (value) => {
        setEnabledInvoiceStatuses(
            invoiceFilterOptions.reduce((acc, item) => {
                acc[item.key] = value;
                return acc;
            }, {})
        );
    };

    return (
        <div className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
                <div>
                    <h2 className="text-lg font-bold text-gray-900">Monthly Invoices</h2>
                    <p className="text-sm text-gray-500">
                        Showing invoice status by scheduled quote date.
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <div ref={filterRef} className="relative">
                        <button
                            type="button"
                            onClick={() => setFilterOpen((prev) => !prev)}
                            className="w-full sm:w-auto flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm font-semibold text-gray-700"
                        >
                            <FiFilter />
                            Invoice Status
                            <span className="text-xs text-gray-400">
                                {enabledCount}/{invoiceFilterOptions.length}
                            </span>
                        </button>

                        {filterOpen && (
                            <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-xl shadow-xl z-30 p-3">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-bold uppercase text-gray-500">
                                        Filter Statuses
                                    </span>

                                    <div className="flex gap-2 text-xs font-semibold">
                                        <button
                                            type="button"
                                            onClick={() => setAllFilters(true)}
                                            className="text-blue-600 hover:text-blue-800"
                                        >
                                            All
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setAllFilters(false)}
                                            className="text-gray-500 hover:text-gray-800"
                                        >
                                            None
                                        </button>
                                    </div>
                                </div>

                                <div className="space-y-1 max-h-80 overflow-y-auto pr-1">
                                    {invoiceFilterOptions.map((item) => {
                                        const active = enabledInvoiceStatuses[item.key] !== false;
                                        const style = INVOICE_STYLES[item.key] || INVOICE_STYLES.not_invoiced;

                                        return (
                                            <button
                                                key={item.key}
                                                type="button"
                                                onClick={() => toggleInvoiceStatus(item.key)}
                                                className={`w-full flex items-center justify-between gap-3 rounded-lg px-2 py-2 text-left transition-colors ${active ? "bg-gray-50" : "bg-white opacity-50"
                                                    } hover:bg-gray-100`}
                                            >
                                                <span
                                                    className={`inline-flex items-center gap-1 text-[10px] py-[2px] px-2 rounded uppercase font-semibold ${style}`}
                                                >
                                                    {item.label}
                                                </span>

                                                <span
                                                    className={`w-5 h-5 rounded-md border flex items-center justify-center ${active
                                                        ? "bg-blue-600 border-blue-600 text-white"
                                                        : "bg-white border-gray-300 text-transparent"
                                                        }`}
                                                >
                                                    <FiCheck className="w-3 h-3" />
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setSelectedMonth((prev) => addMonths(prev, -1))}
                            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"
                        >
                            <FiChevronLeft />
                        </button>

                        <DatePicker
                            selected={selectedMonth}
                            onChange={(date) => setSelectedMonth(startOfMonth(date))}
                            dateFormat="MMMM yyyy"
                            showMonthYearPicker
                            className="w-40 text-center border border-gray-200 rounded-lg px-3 py-2 text-sm font-semibold"
                        />

                        <button
                            type="button"
                            onClick={() => setSelectedMonth((prev) => addMonths(prev, 1))}
                            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"
                        >
                            <FiChevronRight />
                        </button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-500">
                    Loading monthly invoices...
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
                    {days.map((day) => {
                        const key = format(day, "yyyy-MM-dd");
                        const dayAssignments = assignmentsByDay[key] || [];

                        return (
                            <div
                                key={key}
                                className="h-[220px] bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col"
                            >
                                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50">
                                    <div>
                                        <div className="text-xs font-bold text-gray-400 uppercase">
                                            {format(day, "EEE")}
                                        </div>
                                        <div className="text-lg font-bold text-gray-900 leading-none">
                                            {format(day, "d")}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        {dayAssignments.length > 0 && (
                                            <span className="text-[10px] font-bold text-gray-500 bg-white border border-gray-200 rounded-full px-2 py-0.5">
                                                {dayAssignments.length}
                                            </span>
                                        )}
                                        <FiCalendar className="text-gray-300" />
                                    </div>
                                </div>

                                <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
                                    {dayAssignments.length === 0 ? (
                                        <p className="text-xs text-gray-300 italic">No jobs</p>
                                    ) : (
                                        dayAssignments.map((assignment) => {
                                            const style =
                                                INVOICE_STYLES[assignment.invoiceStatusKey] ||
                                                INVOICE_STYLES.not_invoiced;

                                            return (
                                                <div
                                                    key={assignment.quote_info.id}
                                                    className="border flex flex-wrap items-center justify-between gap-1 border-gray-100 rounded-lg p-1 bg-gray-50"
                                                >
                                                    {assignment.is_cancelled && (
                                                        <span className="text-[10px] py-[1px] px-1.5 rounded uppercase font-semibold text-white bg-red-600">
                                                            Cancelled
                                                        </span>
                                                    )}

                                                    <div className="text-xs font-bold text-gray-800 truncate">
                                                        {assignment.quote_info?.child_unique_id || assignment.unique_id}
                                                    </div>


                                                    <span
                                                        className={`inline-flex items-center gap-1 text-[10px] py-[1px] px-1.5 rounded uppercase font-semibold ${style}`}
                                                    >
                                                        {getInvoiceSourceIcon(assignment.quote_info, company)}
                                                        {assignment.invoiceStatusText}
                                                    </span>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default MonthlyInvoiceView;